import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, CheckCircle2, XCircle, Star, Award, BookOpen } from 'lucide-react';
import { SiKakaotalk } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { IMAGES } from '@/assets/images';

const springConfig = { type: 'spring', stiffness: 300, damping: 30 };

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: springConfig
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.08 } }
};

const curriculum = [
  { step: 1, title: '권리관계 & 위험 매물 필터링', desc: '등기부등본 분석, 위험 매물 사전 차단 전략' },
  { step: 2, title: '매물 확보 & 상담 스킬', desc: '임대인 설득 화법, 매물 확보 실전 대화 구조' },
  { step: 3, title: '상권 분석 & 인허가 조건 분석', desc: '업종별 입지 조건, 인허가 체크리스트' },
  { step: 4, title: '고객 상담 & 브리핑 구조', desc: '임차인 니즈 파악법, 신뢰 구축 브리핑 스크립트' },
  { step: 5, title: '권리금 & 협상 실무', desc: '권리금 산정 기준, 쌍방 협상 중재 전략' },
  { step: 6, title: '계약서 작성 & 특약 실무', desc: '상가 특약 필수 조항, 분쟁 예방 계약서 작성법' },
  { step: 7, title: '중개보수 외 보수 청구', desc: '컨설팅 수수료, 추가 보수 합법 청구 구조' },
  { step: 8, title: '부가세 & 포괄양도양수', desc: '세금 구조 이해, 양수도 시 회계 처리' },
  { step: 9, title: '계약 이후 관리', desc: '사후 관리로 재계약·소개 유도 전략' }
];

const faqs = [
  {
    q: '주거 중개 경험만 있어도 따라갈 수 있나요?',
    a: '네, 가능합니다. 이 강의는 주거 중개 경험자가 상가·사무실로 확장할 수 있도록 설계되었습니다. 기초부터 차근차근 설명합니다.'
  },
  {
    q: 'VOD 수강 기간은 어떻게 되나요?',
    a: '평생 수강 가능합니다. 한 번 결제하면 언제든지 반복해서 시청하실 수 있습니다.'
  },
  {
    q: '이프로 시크릿 중개실무와 어떻게 다른가요?',
    a: '시크릿 중개실무는 전반적인 중개 포인팅 전략을 다루는 Zoom Live 강의입니다. 본 강의는 상가·사무실에 특화된 실전 내용을 VOD로 제공합니다. 두 강의를 함께 수강하면 효과가 극대화됩니다.'
  },
  {
    q: '환불은 어떻게 되나요?',
    a: '교재 발송 전까지 100% 환불 가능합니다. 교재는 강의 시작 약 1주일 전에 발송됩니다.'
  },
  {
    q: '강의 자료를 제공하나요?',
    a: '강의 자료는 1주일 전 교재로 발송됩니다. 각종 서식은 PDF로 제공될 예정입니다.'
  }
];

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCTA = () => {
    window.open('https://leepro.liveklass.com/cu/2MgnBxEd', '_blank');
  };

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/95 backdrop-blur-md shadow-lg border-b border-accent/20' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="text-lg md:text-2xl font-bold text-accent">
            중개는 이프로다!
          </motion.div>

          <nav className="hidden md:flex items-center gap-6">
            {['#hero:홈', '#curriculum:커리큘럼', '#instructor:강사소개', '#pricing:가격', '#faq:FAQ'].map(item => {
              const [href, label] = item.split(':');
              return <a key={href} href={href} className="text-sm font-medium hover:text-accent transition-colors">{label}</a>;
            })}
            <a href="tel:010-8464-3995" className="text-sm font-semibold hover:text-accent flex items-center gap-1">
              <Phone className="w-4 h-4" />010-8464-3995
            </a>
          </nav>

          <Button onClick={handleCTA} className="hidden md:flex bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg text-sm px-4 py-2">
            지금 신청하기
          </Button>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-foreground p-1">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden bg-card border-t border-border">
              <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
                {['#hero:홈', '#curriculum:커리큘럼', '#instructor:강사소개', '#pricing:가격', '#faq:FAQ'].map(item => {
                  const [href, label] = item.split(':');
                  return <a key={href} href={href} onClick={() => setMobileMenuOpen(false)} className="text-base font-medium hover:text-accent transition-colors">{label}</a>;
                })}
                <a href="tel:010-8464-3995" className="text-base font-semibold hover:text-accent flex items-center gap-2">
                  <Phone className="w-4 h-4" />010-8464-3995
                </a>
                <Button onClick={() => { handleCTA(); setMobileMenuOpen(false); }} className="bg-accent text-accent-foreground hover:bg-accent/90 w-full text-base py-5">지금 신청하기</Button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO ── */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 bg-gradient-to-br from-background via-background to-card">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.COMMERCIAL_BUILDING_4} alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/70 to-transparent" />
        </div>
        <div className="absolute top-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-accent/10 rounded-full blur-3xl z-0 pointer-events-none" />

        <div className="container mx-auto px-4 md:px-6 py-10 md:py-16 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">

            {/* 텍스트 영역 */}
            <motion.div {...fadeInUp} className="space-y-5 md:space-y-7">
              <Badge className="bg-accent/20 text-accent border-accent/30 text-xs md:text-sm px-3 py-1.5 rounded-full font-semibold">
                🚀 2026년 5월 말 런칭
              </Badge>

              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight">
                상가·사무실 중개,<br />
                이제 <span className="text-accent">실전 구조</span>로<br />
                배우세요
              </h1>

              <p className="text-sm md:text-lg text-muted-foreground leading-relaxed">
                주거 중개와는 완전히 다른 상가·사무실의 세계.<br />
                권리관계 분석부터 계약까지, 현장에서 바로 쓰는 구조를 담았습니다.
              </p>

              <div className="grid grid-cols-3 gap-2 md:gap-4">
                {[
                  { label: '런칭 특가', value: '399,000원' },
                  { label: '수강 기간', value: '평생 가능' },
                  { label: '선착순 한정', value: '지금 신청' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-card/60 backdrop-blur-sm border border-accent/30 rounded-xl p-3 md:p-5 hover:border-accent/60 transition-all">
                    <div className="text-xs md:text-sm text-muted-foreground mb-1">{label}</div>
                    <div className="text-sm md:text-xl font-bold text-accent">{value}</div>
                  </div>
                ))}
              </div>

              <Button onClick={handleCTA} size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base md:text-xl px-8 md:px-10 py-5 md:py-7 shadow-2xl shadow-accent/40 hover:scale-105 transition-all w-full md:w-auto">
                지금 바로 신청하기
              </Button>
            </motion.div>

            {/* 강사 이미지 */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springConfig, delay: 0.2 }}
              className="relative flex justify-center items-end mt-4 lg:mt-0"
            >
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 md:w-96 h-64 md:h-96 bg-gradient-to-t from-accent/20 via-accent/10 to-transparent rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-56 md:w-80 h-56 md:h-80 border-2 border-accent/20 rounded-full" />

              <img
                src={IMAGES.PROFILE_1_NOBG}
                alt="이소연 강사"
                className="relative z-10 w-full max-w-xs md:max-w-md h-auto drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 0 30px rgba(212,175,55,0.3))' }}
              />

              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute top-4 right-2 md:top-8 md:right-4 bg-card border border-accent/40 rounded-xl px-3 py-2 shadow-xl z-20"
              >
                <div className="text-xs md:text-sm font-bold text-accent">11년차 현직</div>
                <div className="text-xs text-muted-foreground">공인중개사</div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                className="absolute bottom-12 left-2 md:bottom-16 md:left-4 bg-card border border-accent/40 rounded-xl px-3 py-2 shadow-xl z-20"
              >
                <div className="text-xs md:text-sm font-bold text-accent">상가·사무실</div>
                <div className="text-xs text-muted-foreground">중개 1타 강사</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── PAIN POINTS ── */}
      <section id="pain" className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3">혹시 이런 고민, 하고 계신가요?</h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid md:grid-cols-3 gap-4 md:gap-6 mb-10">
            {[
              '계약까지 가는 과정이 막혀서 중도 이탈이 반복된다',
              '주거 중개만 해왔는데 상가·사무실로 영역을 넓히고 싶은데 구조가 달라 시작이 막힌다',
              '이론은 알겠는데 실전에서 어떻게 적용해야 할지 모르겠다'
            ].map((pain, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="p-6 md:p-8 bg-background border-destructive/20 hover:border-destructive/50 transition-all h-full">
                  <XCircle className="w-10 h-10 md:w-12 md:h-12 text-destructive mb-4" />
                  <p className="text-sm md:text-base lg:text-lg leading-relaxed font-medium">{pain}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="text-center">
            <p className="text-base md:text-xl lg:text-2xl font-bold text-accent">
              → 상가·사무실은 수익이 다른 시장입니다. 제대로 배우면, 달라집니다.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CURRICULUM ── */}
      <section id="curriculum" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3">9단계 실전 커리큘럼</h2>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
              각 강의는 약 20분 내외로, 출퇴근 시간이나 짧은 대기 시간에도 학습 가능합니다. ✅
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {curriculum.map((item) => (
              <motion.div key={item.step} variants={fadeInUp}>
                <Card className="p-5 md:p-6 bg-card border-accent/20 hover:border-accent/50 transition-all h-full hover:shadow-lg hover:shadow-accent/10">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/20 border-2 border-accent flex items-center justify-center">
                      <span className="text-accent font-bold text-sm md:text-base">{item.step}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-sm md:text-base lg:text-lg mb-1.5">{item.title}</h3>
                      <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── INSTRUCTOR ── */}
      <section id="instructor" className="py-16 md:py-24 bg-gradient-to-br from-card via-card to-background overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-14 items-center">

            {/* 강사 이미지 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="relative flex justify-center items-end"
            >
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 md:w-96 h-64 md:h-96 bg-gradient-to-t from-accent/25 via-accent/10 to-transparent rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-56 md:w-80 h-56 md:h-80 border-2 border-accent/25 rounded-full" />

              <img
                src={IMAGES.PROFILE_2_NOBG}
                alt="이소연 강사 프로필"
                className="relative z-10 w-full max-w-xs md:max-w-lg h-auto drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 8px 40px rgba(212,175,55,0.35))' }}
              />

              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute top-6 right-2 bg-background/90 backdrop-blur border border-accent/40 rounded-xl p-3 shadow-xl z-20"
              >
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-accent fill-accent" />
                  <div>
                    <div className="text-xs md:text-sm font-bold text-foreground">11년차 현직 중개사</div>
                    <div className="text-xs text-muted-foreground">법인 대표</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.7 }}
                className="absolute bottom-16 left-2 bg-background/90 backdrop-blur border border-accent/40 rounded-xl p-3 shadow-xl z-20"
              >
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-accent" />
                  <div>
                    <div className="text-xs md:text-sm font-bold text-foreground">중개 1타 강사</div>
                    <div className="text-xs text-muted-foreground">이프로다!</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* 강사 소개 텍스트 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="space-y-5"
            >
              <Badge className="bg-accent/20 text-accent border-accent/30 text-xs md:text-sm px-3 py-1.5 rounded-full font-semibold">
                중개 1타 공인중개사
              </Badge>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                이소연
              </h2>

              <p className="text-base md:text-xl font-bold text-accent">중개는 이프로다!</p>

              <div className="space-y-3">
                {[
                  '투데이부동산 중개 법인 본점 대표',
                  "유튜버 '담백한경제이프로' 운영",
                  '11년차 베테랑 현직 공인중개사',
                  '(사)한국부동산융복합학회 이사',
                  '현직 투자 분석사'
                ].map((career, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-accent flex-shrink-0" />
                    <span className="text-sm md:text-base lg:text-lg">{career}</span>
                  </div>
                ))}
              </div>

              <Card className="p-5 md:p-6 bg-accent/10 border-accent/30">
                <p className="text-sm md:text-base lg:text-lg leading-relaxed italic font-medium">
                  "막연하게 고민만 하지 말고, 계약까지 본부를 가져가세요.<br />
                  중개는, 아는 사람이 결과를 만듭니다."
                </p>
              </Card>
            </motion.div>
          </div>

          {/* 저서 소개 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-16 md:mt-24"
          >
            <div className="bg-gradient-to-r from-accent/10 via-background to-accent/10 border border-accent/20 rounded-2xl md:rounded-3xl p-6 md:p-12">
              <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
                <div className="space-y-4 md:space-y-6">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-7 h-7 md:w-9 md:h-9 text-accent" />
                    <h3 className="text-xl md:text-2xl font-bold text-accent">저서 소개</h3>
                  </div>
                  <h4 className="text-2xl md:text-3xl font-bold leading-tight">
                    나만 알고 싶은<br /><span className="text-accent">중개실무 시크릿북</span>
                  </h4>
                  <p className="text-sm md:text-base lg:text-lg text-muted-foreground leading-relaxed">
                    현장에서 직접 쌓은 11년의 경험을 한 권에 집약.<br />
                    실전 중개의 모든 것을 담은 필독서입니다.
                  </p>
                  <div className="flex items-center gap-2">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-5 h-5 md:w-6 md:h-6 text-accent fill-accent" />)}
                    <span className="text-sm md:text-base font-semibold text-muted-foreground ml-2">현직 중개사들의 선택</span>
                  </div>
                </div>
                <div className="flex justify-center relative">
                  <div className="absolute inset-0 bg-accent/10 rounded-3xl blur-2xl" />
                  <img
                    src={IMAGES.PROFILE_3_NOBG}
                    alt="나만 알고 싶은 중개실무 시크릿북 저자 이소연"
                    className="relative z-10 w-full max-w-[260px] md:max-w-sm h-auto drop-shadow-2xl"
                    style={{ filter: 'drop-shadow(0 8px 32px rgba(212,175,55,0.4))' }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section id="benefits" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3">이런 분께 추천합니다 ✅</h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid md:grid-cols-2 gap-4 md:gap-5 max-w-4xl mx-auto">
            {[
              '상가·사무실 중개로 영역을 확장하고 싶은 중개사',
              '주거+상가 투트랙 운영으로 수입을 구조화하고 싶은 분',
              '중개보수 외 컨설팅 수수료 등 추가 수익원을 만들고 싶은 분',
              '권리금, 인허가 등을 체계적으로 정리하고 싶은 분'
            ].map((benefit, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="p-5 md:p-7 bg-card border-accent/20 hover:border-accent/50 transition-all">
                  <div className="flex items-start gap-3 md:gap-4">
                    <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-accent flex-shrink-0 mt-0.5" />
                    <p className="text-sm md:text-base lg:text-lg leading-relaxed font-medium">{benefit}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-16 md:py-24 bg-gradient-to-b from-card to-background">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Badge className="bg-destructive/20 text-destructive border-destructive/30 text-xs md:text-sm px-4 py-2 mb-4 rounded-full font-bold">
                🔥 런칭 특가 할인 (기간 한정)
              </Badge>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold">VOD 강의 런칭 특가</h2>
            </div>

            <Card className="p-6 md:p-10 bg-card/80 border-accent/30 shadow-2xl shadow-accent/20">
              <div className="text-center mb-8">
                <div className="text-xl md:text-2xl text-muted-foreground line-through mb-2">정가 800,000원</div>
                <div className="flex items-center justify-center gap-3 md:gap-4">
                  <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-accent font-mono">399,000원</div>
                  <Badge className="bg-destructive text-destructive-foreground text-sm md:text-base px-3 py-1.5 rounded-lg font-bold">50% OFF</Badge>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {[
                  '✅ 신청 기간: 2026년 5월 15일까지',
                  '✅ 강의 형태: VOD 녹화 강의 (PC, 모바일 모두 시청 가능)',
                  '✅ 강의 자료: 1주일 전 교재 발송 + 각종 서식 PDF 제공',
                  '✅ 중개사고 0% 특강모음집 (20만원 상당) 무료 제공',
                  '✅ 정규강의 외 기수별 추가 특강 1회 진행',
                  '✅ 평생 수강 가능'
                ].map((item, idx) => (
                  <div key={idx} className="text-sm md:text-base leading-relaxed">{item}</div>
                ))}
              </div>

              <div className="bg-muted/50 rounded-xl p-4 mb-8 text-xs md:text-sm text-muted-foreground space-y-1">
                <p>※ 런칭 특가는 선착순에만 적용됩니다.</p>
                <p>※ 정원 종료 시 정가로 전환됩니다.</p>
              </div>

              <Button onClick={handleCTA} size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 text-base md:text-xl py-6 md:py-7 shadow-xl shadow-accent/40 hover:scale-105 transition-all">
                지금 바로 신청하기
              </Button>

              <div className="text-center mt-5">
                <a href="tel:010-8464-3995" className="text-base md:text-lg font-bold text-accent hover:underline flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />010-8464-3995
                </a>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold">자주 묻는 질문</h2>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="bg-background border border-border rounded-xl px-5 md:px-6">
                  <AccordionTrigger className="text-left text-sm md:text-base font-bold hover:text-accent py-4 md:py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pt-1 pb-4 md:pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section id="cta" className="py-16 md:py-24 bg-gradient-to-b from-background to-card relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.CONTRACT_SIGNING_8} alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>
        <div className="absolute right-0 bottom-0 h-full z-0 opacity-10 pointer-events-none hidden lg:block">
          <img src={IMAGES.PROFILE_1_NOBG} alt="" className="h-full w-auto object-contain object-bottom" />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={springConfig} className="text-center max-w-3xl mx-auto space-y-6 md:space-y-8">
            <h2 className="text-2xl md:text-4xl lg:text-6xl font-bold leading-tight">
              중개는 결국,<br />
              <span className="text-accent">아는 사람이 결과를 만듭니다.</span>
            </h2>

            <p className="text-sm md:text-lg lg:text-xl text-muted-foreground">
              혼자 고민하지 마세요. 이프로와 함께 시작하세요.
            </p>

            <p className="text-base md:text-xl font-bold text-accent">
              더 이상 상가·사무실 문의를 놓치지 마세요.<br />
              배우면, 계약이 됩니다.
            </p>

            <div className="space-y-4 pt-2">
              <Button onClick={handleCTA} size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base md:text-xl px-10 md:px-14 py-6 md:py-7 shadow-2xl shadow-accent/40 hover:scale-105 transition-all">
                지금 바로 신청하기
              </Button>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm md:text-base">
                <a href="tel:010-8464-3995" className="font-bold text-accent hover:underline flex items-center gap-2">
                  <Phone className="w-4 h-4" />전화 상담 010-8464-3995
                </a>
                <span className="hidden sm:inline text-muted-foreground">|</span>
                <span className="text-muted-foreground">카카오톡 상담도 가능합니다</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 md:py-14 bg-card border-t border-border">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center space-y-3 md:space-y-4">
            <div className="text-xl md:text-2xl font-bold text-accent">중개는 이프로다!</div>
            <div className="space-y-2">
              <p className="text-sm md:text-base font-semibold">문의 및 상담</p>
              <a href="tel:010-8464-3995" className="text-base md:text-xl font-bold text-accent hover:underline flex items-center justify-center gap-2">
                <Phone className="w-5 h-5" />010-8464-3995
              </a>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground">차이나는 남다른 클래스, 중개는 이프로다!</p>
            <p className="text-xs text-muted-foreground">© 2026 이프로 중개실무 VOD. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ── 모바일 하단 고정 버튼 ── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-card/95 backdrop-blur border-t border-border shadow-2xl">
        <div className="px-3 py-3 flex gap-2">
          <Button onClick={handleCTA} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 text-sm py-5 font-bold">
            <Phone className="w-4 h-4 mr-1.5" />지금 신청하기
          </Button>
          <Button onClick={() => window.open('https://pf.kakao.com', '_blank')} variant="outline" className="flex-1 border-accent text-accent hover:bg-accent/10 text-sm py-5 font-bold">
            <SiKakaotalk className="w-4 h-4 mr-1.5" />카카오 상담
          </Button>
        </div>
      </div>

      {/* 모바일 하단 버튼 높이만큼 여백 */}
      <div className="h-16 md:hidden" />
    </div>
  );
}
